import React from "react";
import { Link } from "react-router-dom";
import { Badge, UncontrolledTooltip } from "reactstrap";
import moment from "moment";
import { Icon } from "../../Component";
import { getPublicUrl } from "../../../utils/Utils";
import PartnerStatusBadge from "./PartnerStatusBadge";
import {
  partnerOffersMedicalTourism,
  normalizeTcApprovalStatus,
  tcApprovalSelectValueFromPartner,
  tcApprovalApiStatusFromSelectValue,
  PARTNER_TC_PLATFORM_SELECT_OPTIONS,
  PARTNER_TM_PROGRAM_SELECT_OPTIONS,
  PARTNER_WORKFLOW_OPTIONS,
} from "./medicalPartnerAdminConstants";
import { isActiveStatus, partnerWorkflowStatus } from "./medicalPartnerListTableHelpers";

function ReadinessChip({ label, ok, na = false, tooltip }) {
  const color = na ? "#b6c2d1" : ok ? "#1ee0ac" : "#f4bd0e";
  const bg = na ? "#f5f7fb" : ok ? "#ebfaf3" : "#fef9ec";
  const border = na ? "#dce2ec" : ok ? "#b6ecd4" : "#fbdfa2";
  return (
    <span
      className="partner-details-overview__readiness-chip"
      style={{ background: bg, border: `1px solid ${border}`, color }}
      title={tooltip}
      aria-label={`${label}: ${na ? "N/A" : ok ? "ok" : "not ready"}`}
    >
      <span
        className="partner-details-overview__readiness-dot"
        style={{ background: color }}
        aria-hidden="true"
      />
      {label}
    </span>
  );
}

function tcBadge({ on, pending, onLabel, pendingLabel, offLabel }) {
  if (pending) {
    return (
      <Badge className="badge-dim" color="warning">
        {pendingLabel}
      </Badge>
    );
  }
  if (on) {
    return (
      <Badge className="badge-dim" color="success">
        {onLabel}
      </Badge>
    );
  }
  return (
    <Badge className="badge-dim" color="light">
      {offLabel}
    </Badge>
  );
}

const MpOverviewStatus = ({
  partner,
  hasUser,
  loginEmail,
  primaryDirectoryUserId = "",
  primaryUserLoginActive = null,
  enableUserLoginBusy = false,
  onEnableUserLogin,
  totalClinics = 0,
  partnerSlugForUrl = "",
  tcSaving,
  mtSaving,
  workflowBusy,
  canEditPrograms,
  canEditWorkflow,
  onTcChange,
  onMtChange,
  onWorkflowChange,
}) => {
  const tcApi = normalizeTcApprovalStatus(partner);
  const tcOn = tcApi === "approved";
  const tcPending = tcApi === "pending";
  const tcSelect = tcApprovalSelectValueFromPartner(partner);
  const live = isActiveStatus(partner);
  const workflow = partnerWorkflowStatus(partner);
  const mtOn = partnerOffersMedicalTourism(partner);
  const statusGlossaryId = partner?._id ? `partner-status-glossary-${partner._id}` : "partner-status-glossary";
  const partnerAccountAria =
    hasUser && primaryUserLoginActive === false
      ? "Partner account — linked, login disabled for primary directory user"
      : "Partner account — primary directory user";

  return (
    <div className="card card-bordered mb-4">
      <div className="card-inner">
        <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
          <h6 className="overline-title mb-0">Partner-level · Status</h6>
          <span
            id={statusGlossaryId}
            className="d-inline-flex align-items-center"
            role="button"
            tabIndex={0}
            aria-label="Status section glossary"
            style={{ cursor: "help" }}
          >
            <Icon name="info-fill" className="text-soft" style={{ fontSize: "1rem" }} />
          </span>
          <UncontrolledTooltip placement="top" target={statusGlossaryId}>
            TC: platform programme approval. Go live: partner workflow. Partner account: link to the directory user. Patient
            booking also needs clinics to be ready — use Teleconsultation and Clinics tabs.
          </UncontrolledTooltip>
        </div>
        <p className="text-soft small mb-3 mb-md-2">
          A partner record must be linked to a real directory user (primary account). Without it, clinics and teleconsultation setup stay blocked.
        </p>
        <div className="partner-details-overview__status-strip">
          <div className="partner-details-overview__status-tile" role="group" aria-label="Teleconsultation platform">
            <div className="partner-details-overview__status-tile-label">TC</div>
            <div className="partner-details-overview__status-tile-value">
              {tcBadge({
                on: tcOn,
                pending: tcPending,
                onLabel: "On",
                pendingLabel: "Pending",
                offLabel: "Off",
              })}
            </div>
            <div className="partner-details-overview__status-tile-meta text-soft small">
              Platform TC approval is separate from partner workflow status.
            </div>
            {canEditPrograms ? (
              <div className="partner-details-overview__status-tile-select mt-2">
                <label className="sr-only" htmlFor="partner-overview-tc-platform">
                  Teleconsultation platform status
                </label>
                <select
                  id="partner-overview-tc-platform"
                  className="form-control form-control-sm"
                  value={tcSelect}
                  disabled={tcSaving}
                  onChange={(e) => {
                    const next = tcApprovalApiStatusFromSelectValue(e.target.value);
                    if (next === normalizeTcApprovalStatus(partner)) return;
                    void onTcChange(next, partner.tcApprovalNote || "");
                  }}
                >
                  {PARTNER_TC_PLATFORM_SELECT_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}
            {partner.tcApprovalUpdatedAt ? (
              <div className="partner-details-overview__status-tile-meta">
                Updated {moment(partner.tcApprovalUpdatedAt).format("DD/MM/YYYY")}
              </div>
            ) : null}
          </div>

          <div className="partner-details-overview__status-tile" role="group" aria-label="Medical tourism program">
            <div className="partner-details-overview__status-tile-label">TM</div>
            <div className="partner-details-overview__status-tile-value">
              {mtOn ? (
                <Badge className="badge-dim" color="success">
                  On
                </Badge>
              ) : (
                <Badge className="badge-dim" color="light">
                  Off
                </Badge>
              )}
            </div>
            {canEditPrograms ? (
              <div className="partner-details-overview__status-tile-select mt-2">
                <label className="sr-only" htmlFor="partner-overview-mt-program">
                  Medical tourism program
                </label>
                <select
                  id="partner-overview-mt-program"
                  className="form-control form-control-sm"
                  value={mtOn ? "on" : "off"}
                  disabled={mtSaving}
                  onChange={(e) => {
                    void onMtChange(e.target.value === "on");
                  }}
                >
                  {PARTNER_TM_PROGRAM_SELECT_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}
            {mtOn && totalClinics === 0 ? (
              <div className="partner-details-overview__status-tile-meta text-soft small">
                No clinic added yet — open Clinics tab.
              </div>
            ) : null}
          </div>

          <div
            className="partner-details-overview__status-tile"
            role="group"
            aria-label={partnerAccountAria}
          >
            <div className="partner-details-overview__status-tile-label">Partner account</div>
            <div className="partner-details-overview__status-tile-value">
              {hasUser ? (
                primaryUserLoginActive === true ? (
                  <Badge className="badge-dim" color="success">
                    Linked · Active
                  </Badge>
                ) : primaryUserLoginActive === false ? (
                  <Badge className="badge-dim" color="warning">
                    Linked · Login blocked
                  </Badge>
                ) : (
                  <Badge className="badge-dim" color="secondary">
                    Linked
                  </Badge>
                )
              ) : (
                <Badge className="badge-dim" color="danger">
                  Not linked
                </Badge>
              )}
            </div>
            {hasUser ? (
              <div className="partner-details-overview__status-tile-meta">
                {primaryDirectoryUserId ? (
                  loginEmail ? (
                    <Link
                      to={getPublicUrl(`user-details-regular/${primaryDirectoryUserId}`)}
                      className="text-break small"
                    >
                      {loginEmail}
                    </Link>
                  ) : (
                    <Link to={getPublicUrl(`user-details-regular/${primaryDirectoryUserId}`)}>
                      Open user profile
                    </Link>
                  )
                ) : loginEmail ? (
                  <span className="text-break small">{loginEmail}</span>
                ) : (
                  <span className="text-soft small">Login email not loaded</span>
                )}
                {primaryUserLoginActive === false && typeof onEnableUserLogin === "function" ? (
                  <div className="partner-details-overview__blocked-login">
                    <span className="partner-details-overview__blocked-login-hint">
                      Partner cannot log in
                    </span>
                    <button
                      type="button"
                      className="partner-details-overview__enable-login-btn"
                      onClick={onEnableUserLogin}
                      disabled={enableUserLoginBusy}
                      aria-label="Restore login access for this user"
                    >
                      {enableUserLoginBusy ? (
                        "Activating…"
                      ) : (
                        <>
                          <Icon name="unlock-fill" style={{ fontSize: "0.85rem" }} />
                          &nbsp;Restore access
                        </>
                      )}
                    </button>
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="partner-details-overview__status-tile-meta text-soft small">
                Required — link a user in Team tab
              </div>
            )}
          </div>

          <div className="partner-details-overview__status-tile" role="group" aria-label="Go live workflow">
            <div className="partner-details-overview__status-tile-label">Go live</div>
            <div className="partner-details-overview__status-tile-value partner-details-overview__fx">
              {live ? (
                <Badge className="badge-dim" color="success" title="Partner workflow is Live (API status active)">
                  Live
                </Badge>
              ) : (
                <PartnerStatusBadge status={partner?.status} />
              )}
            </div>
            {!live ? (
              <div className="partner-details-overview__status-tile-meta text-soft small">
                Workflow is separate from platform TC approval and from clinic pricing or availability.
              </div>
            ) : null}
            {canEditWorkflow ? (
              <div className="partner-details-overview__status-tile-select mt-2">
                <label className="sr-only" htmlFor="partner-overview-workflow">
                  Workflow status
                </label>
                <select
                  id="partner-overview-workflow"
                  className="form-control form-control-sm"
                  value={workflow}
                  disabled={workflowBusy}
                  aria-busy={workflowBusy ? "true" : "false"}
                  onChange={(e) => {
                    void onWorkflowChange(e.target.value);
                  }}
                >
                  {PARTNER_WORKFLOW_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}
          </div>
        </div>
        <div className="partner-details-overview__readiness mt-3 pt-3 border-top">
          <span className="partner-details-overview__readiness-label">Readiness check</span>
          <div className="partner-details-overview__readiness-chips">
            <ReadinessChip
              label="Live"
              ok={live}
              na={false}
              tooltip="Partner workflow is set to active."
            />
            <ReadinessChip
              label="TC"
              ok={tcOn}
              na={!tcOn && !tcPending}
              tooltip={tcOn ? "Platform TC approved." : tcPending ? "Platform TC pending review." : "Platform TC not approved."}
            />
            <ReadinessChip
              label="User active"
              ok={primaryUserLoginActive === true}
              na={primaryUserLoginActive === null}
              tooltip={
                primaryUserLoginActive === true
                  ? "Primary directory user login is active."
                  : primaryUserLoginActive === false
                  ? "Primary directory user login is disabled."
                  : "Primary directory user not loaded."
              }
            />
            <ReadinessChip
              label="Has clinics"
              ok={totalClinics > 0}
              na={false}
              tooltip={totalClinics > 0 ? `${totalClinics} clinic(s) on record.` : "No clinic added yet."}
            />
            <ReadinessChip
              label="Slug"
              ok={Boolean(partnerSlugForUrl)}
              na={false}
              tooltip={partnerSlugForUrl ? `Public path: /partners/${partnerSlugForUrl}` : "No public slug set — patients cannot open the partner page."}
            />
          </div>
          <p className="text-soft small mb-0 mt-2">
            Platform approval, Go live workflow, and patient booking are separate steps.
          </p>
        </div>
      </div>
    </div>
  );
};

export default MpOverviewStatus;
